import React, { useState } from 'react';
import { getCurrentUser, setCurrentUser } from '../utils/auth';
import { updateUser } from '../utils/dataManager';
import { validatePassword } from '../utils/validation';
import './Settings.css';

const Settings = () => {
  const [user, setUser] = useState(getCurrentUser());
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    
    // Clear errors when user types
    if (passwordErrors[e.target.name]) {
      setPasswordErrors({
        ...passwordErrors,
        [e.target.name]: ''
      });
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    
    // Validate passwords
    const errors = {};
    
    if (passwordData.currentPassword !== user.password) {
      errors.currentPassword = 'Current password is incorrect';
    }
    
    if (!validatePassword(passwordData.newPassword)) {
      errors.newPassword = 'New password must be at least 6 characters';
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      setLoading(false);
      return;
    }
    
    try {
      // Update password in storage
      const updatedUser = await updateUser(user.id, { 
        password: passwordData.newPassword 
      });
      
      // Update current user session
      setCurrentUser(updatedUser);
      setUser(updatedUser);
      
      // Reset form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordForm(false);
      setMessage('Password updated successfully!');
      
    } catch (error) {
      setMessage('Error updating password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setShowPasswordForm(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordErrors({});
  };

  return (
    <div className="settings">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account settings and preferences</p>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="settings-sections">
        {/* Account Information */}
        <div className="settings-section">
          <h2>Account Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Username</label>
              <span>{user.username}</span>
            </div>
            <div className="info-item">
              <label>Email</label>
              <span>{user.email}</span>
            </div>
            <div className="info-item">
              <label>Role</label>
              <span className={`role-badge ${user.role}`}>
                {user.role.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div className="info-item">
              <label>Member Since</label>
              <span>{new Date(user.created_at).toLocaleDateString()}</span>
            </div>
            <div className="info-item">
              <label>Last Login</label>
              <span>
                {user.last_login 
                  ? new Date(user.last_login).toLocaleDateString()
                  : 'Never'
                }
              </span>
            </div>
          </div>
        </div>

        {/* Password Management */}
        <div className="settings-section">
          <h2>Password Management</h2>
          <p>Keep your account secure by using a strong password</p>
          
          {!showPasswordForm ? (
            <button 
              onClick={() => setShowPasswordForm(true)}
              className="btn btn-primary"
            >
              Change Password
            </button>
          ) : (
            <form onSubmit={handlePasswordSubmit} className="password-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className={passwordErrors.currentPassword ? 'error' : ''}
                  required
                />
                {passwordErrors.currentPassword && (
                  <span className="error-text">{passwordErrors.currentPassword}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className={passwordErrors.newPassword ? 'error' : ''}
                  required
                />
                {passwordErrors.newPassword && (
                  <span className="error-text">{passwordErrors.newPassword}</span>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className={passwordErrors.confirmPassword ? 'error' : ''}
                  required
                />
                {passwordErrors.confirmPassword && (
                  <span className="error-text">{passwordErrors.confirmPassword}</span>
                )}
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCancelPasswordChange}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Application Info */}
        <div className="settings-section">
          <h2>Application Information</h2>
          <div className="app-info">
            <div className="info-item">
              <label>Application Name</label>
              <span>Digital ID Management System</span>
            </div>
            <div className="info-item">
              <label>Version</label>
              <span>1.0.0</span>
            </div>
            <div className="info-item">
              <label>Company</label>
              <span>© Jethro.inc - PhyGital ( Physical & Digital Agency )</span>
            </div>
          </div>
        </div>

        {/* Role Permissions */}
        <div className="settings-section">
          <h2>Your Permissions</h2>
          <div className="permissions-list">
            <div className="permission-item allowed">
              <span className="permission-name">Create Digital Cards</span>
              <span className="permission-status allowed">✓ Allowed</span>
            </div>
            <div className="permission-item allowed">
              <span className="permission-name">Edit Digital Cards</span>
              <span className="permission-status allowed">✓ Allowed</span>
            </div>
            <div className="permission-item allowed">
              <span className="permission-name">Publish/Unpublish Cards</span>
              <span className="permission-status allowed">✓ Allowed</span>
            </div>
            <div className={`permission-item ${
              user.role === 'super_admin' || user.role === 'admin' ? 'allowed' : 'denied'
            }`}>
              <span className="permission-name">Manage Users</span>
              <span className={`permission-status ${
                user.role === 'super_admin' || user.role === 'admin' ? 'allowed' : 'denied'
              }`}>
                {user.role === 'super_admin' || user.role === 'admin' ? '✓ Allowed' : '✗ Denied'}
              </span>
            </div>
            <div className={`permission-item ${
              user.role === 'super_admin' ? 'allowed' : 'denied'
            }`}>
              <span className="permission-name">System Administration</span>
              <span className={`permission-status ${
                user.role === 'super_admin' ? 'allowed' : 'denied'
              }`}>
                {user.role === 'super_admin' ? '✓ Allowed' : '✗ Denied'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
